import { createSlice } from "@reduxjs/toolkit";
import {
  type PostState, type Errors, type VoteOption,
  initialVoteSettingErrors
} from "../util/createPost"

export const initialErrors: Errors = {
  title: "",
  category: "",
  group: "",
  details: "",
  tags: "",
  images: "",
  attachments: "",
  acceptVotes: "",
  acceptComments: "",
  agree: "",
  date: "",
  voteSetting: {
    voteQuestion: "",
    voteOptions: [],
    expiryDate: "",
    limitVoteOptions: "",
  },
}

const initialValues: PostState = {
  user: {},
  title: "",
  category: "",
  group: "",
  tags: [],
  details: "",
  images: [],
  attachments: [],
  acceptComments: false,
  acceptVotes: false,
  showNameOption: 1,
  isPrivate: false,
  privateUsers: [],
  voteSetting: {
    voteQuestion: "",
    voteOptions: [],
    expiryDate: undefined,
  },
  changabled: {
    voteSetting: true,
  }
}

type State = {
  values: PostState,
  errors: Errors,
}

const initialState: State = {
  values: initialValues,
  errors: initialErrors
}

const createOpinionReducer = createSlice({
  name: "opinion",
  initialState,
  reducers: {
    reset: () => initialState,
    initialize: (state) => {
      state.values = initialValues
      state.errors = initialErrors
    },
    setProps: (state, action) => {
      state.values = formatState(action.payload)
      state.errors = initialEditPostErrors(action.payload)
    },
    setItem: (state, action) => {
      const value: any = {};
      value[action.payload.key] = action.payload.value;
      state.values = {
        ...state.values,
        ...value
      };

      const error: any = {};
      error[action.payload.key] = action.payload.error ?? ''
      state.errors = {
        ...state.errors,
        ...error
      };
    },
    setPrivateUsers: (state, action) => {
      state.values.privateUsers = action.payload.value;
    },
    setVoteSetting: (state, action) => {
      const value: any = {};
      value[action.payload.key] = action.payload.value;
      state.values.voteSetting = {
        ...state.values.voteSetting,
        ...value
      };

      const error: any = {};
      error[action.payload.key] = action.payload.error ?? ''
      state.errors.voteSetting = {
        ...state.errors.voteSetting,
        ...error
      };
    },
    setVoteOptions: (state, action) => {
      const options = action.payload.value
      state.values.voteSetting.voteOptions = options;
      state.errors.voteSetting =
        action.payload.error ?
          { ...state.errors.voteSetting, voteOptions: action.payload.error } :
          initialVoteSettingErrors(options.filter((option: VoteOption) => option._destroy === 0).length);
    },
    setErrors: (state, action) => {
      state.errors = {
        ...state.errors,
        ...action.payload
      }
    }
  }
});

export const {
  reset,
  initialize,
  setProps,
  setItem,
  setPrivateUsers,
  setVoteSetting,
  setVoteOptions,
  setErrors,
} = createOpinionReducer.actions;

export default createOpinionReducer.reducer;

const formatState = (post: any) => {
  const voteOptions = post.vote_options

  const isVoted = voteOptions.some((vote: any) => vote.votes_count > 0)
  const voteOptionValues = voteOptions.map((option: any) => {
    return {
      id: option.id,
      optionText: option.option_text,
      isVoted: isVoted,
      key: crypto.randomUUID(),
      _destroy: 0,
    }
  })

  const postTags = post.post_tags.map((tag: any) => {
    return {
      id: tag.id,
      tag: tag.tag,
      _destroy: 0,
    }
  })

  const privateUsers = post.post_private_users.map((privateUser: any) => {
    return {
      id: privateUser.id, // post_private_user.id
      identifier: crypto.randomUUID(),
      userId: privateUser.privater_id,
      userType: privateUser.privater_type,
      name: privateUser.name,
      nickname: privateUser.nickname,
      image: privateUser?.image ?? "",
      _destroy: 0,
    }
  })

  const postValues: any = {
    id: post.id,
    creator: post?.creator,
    title: post.title,
    details: post.details,
    group: String(post.group.id),
    category: String(post.category.id),
    showNameOption: post.is_anonymous,
    acceptComments: post.enable_comments,
    acceptVotes: post.enable_participants,
    isPrivate: post.is_private,
    privateUsers: privateUsers,
    tags: postTags,
    images: post.images,
    attachments: post.attachments,
    voteSetting: {
      voteQuestion: post.vote_question || "",
      expiryDate: post.expiry_date ?? undefined,
      voteOptions: voteOptionValues,
    },
    changabled: {
      voteSetting: !isVoted,
    }
  }

  return {...initialValues, ...postValues}
};

const initialEditPostErrors = (post: any) => {
  const errors: Errors = Object.assign({}, JSON.parse(JSON.stringify(initialErrors)));
  errors.voteSetting = initialVoteSettingErrors(post.vote_options.length)
  return errors
}
